<template>
  <div>
    <v-alert
      dense
      outlined
      type="error"
      class="body-2"
      v-if="messages.detail"
    >{{ messages.detail }}
    </v-alert
    >
    <ValidationProvider
      name="files"
      mode="lazy"
      ref="provider"
      rules="required_file|ext:png,jpg,jpeg,bmp,pdf|size:20480"
      v-slot="{ errors }"
    >
      <v-file-input
        label="Выбрать файл"
        accept="image/png, image/jpg, image/jpeg, image/bmp, .pdf"
        :prepend-icon="icons.mdiPaperclip"
        :multiple="multiple"
        class="lighten-1 indigo--text"
        v-model="files"
        :error-messages="errors"
        @change="onChange"
        :disabled="uploading"
        :clearable="clearable"
        :loading="uploading"
      >
      </v-file-input>
    </ValidationProvider>

    <v-list v-if="uploadedFiles[type] && uploadedFiles[type].length" dense max-width="300px">
      <v-subheader>Прикрепленные файлы:</v-subheader>
      <v-list-item
        link
        v-for="(item, index) in uploadedFiles[type]"
        :key="index"
        :href="'/files/' + item.name"
        target="_blank"
      >
        <v-list-item-avatar class="hidden-md-and-down">
          <v-icon v-if="item.filetype === 'pdf'">{{icons.mdiFilePdfOutline}}</v-icon>
          <v-icon v-else>{{icons.mdiFileImageOutline}}</v-icon>
        </v-list-item-avatar>

        <v-list-item-content>
          <v-list-item-title v-text="item.chip_name"></v-list-item-title>
          <v-list-item-subtitle class="text-caption font-weight-regular">Размер: {{item.size}}</v-list-item-subtitle>
        </v-list-item-content>

        <v-list-item-action>
          <v-row>
            <v-btn icon @click.prevent="deleteFile(type, index)">
              <v-icon color="grey lighten-1">{{icons.mdiClose}}</v-icon>
            </v-btn>
          </v-row>

        </v-list-item-action>
      </v-list-item>
    </v-list>
  </div>
</template>

<script>
  import {extend, localize, ValidationProvider} from "vee-validate";
  import {ext, image, required, size} from "vee-validate/dist/rules";
  import {
    mdiClose,
    mdiFileImageOutline,
    mdiFilePdfOutline,
    mdiFile,
    mdiHandPointingRight,
    mdiMagnifyPlusOutline,
    mdiPaperclip,
    mdiSend,
    mdiTrashCanOutline
  } from "@mdi/js";
  import {mapGetters} from "vuex";

  export default {
    name: "FileInput",
    components: {
      ValidationProvider
    },
    props: {
      type: {
        type: Number,
        default: 0
      },
      application: {
        type: Object,
        default: null
      },
      multiple: {
        type: Boolean,
        default: false
      },
      validate: {
        type: Boolean,
        default: true
      },
      maxFiles: {
        type: Number,
        default: 5
      },
    },
    data() {
      return {
        icons: {
          mdiClose,
          mdiFileImageOutline,
          mdiFilePdfOutline,
          mdiFile,
          mdiPaperclip,
          mdiHandPointingRight,
          mdiMagnifyPlusOutline,
          mdiTrashCanOutline,
          mdiSend,
        },
        files: [],
        snackbar: false,
        isSuccess: true,
        uploading: false,
        clearable: false,
      };
    },
    async created() {
      if (this.application === null) {
        await this.$store.dispatch("filemanager/loadUploadedFiles");
      }
    },
    computed: {
      ...mapGetters("context", ["isLoading", "messages"]),
      ...mapGetters("filemanager", ["uploadedFiles"]),
    },
    watch: {
      async application() {
        await this.$store.dispatch("filemanager/loadFilesByApplication", this.application.id);
      }
    },
    methods: {
      async deleteFile(type, key) {
        await this.$store.dispatch("filemanager/deleteFile", {type: type, key: key});
      },
      async validateFiles() {
        if (!this.validate) {
          this.$refs.provider.reset();
          this.isSuccess = true;
          return true;
        }

        const uploadedFiles = this.uploadedFiles[this.type] || []
        if (uploadedFiles.length && this.files.length === 0) {
          this.$refs.provider.reset();
          this.isSuccess = true;
          this.uploading = false;
          return true;
        }

        if (uploadedFiles.length === 0 && this.files.length) {
          this.$refs.provider.setErrors(['Необходимо прикрепить выбранный файл.']);
          this.isSuccess = false;
          return true;
        }

        if (this.validate) {
          this.isSuccess = false;
          const {valid} = await this.$refs.provider.validate(this.files);
          return valid;
        }
      },
      async onChange() {
        this.clearable = false

        if (!this.validate) {
          this.$refs.provider.reset();
          this.isSuccess = true;
          return;
        }

        this.uploading = true
        const uploadedFiles = this.uploadedFiles[this.type] || []
        if (uploadedFiles.length && this.files.length === 0) {
          this.uploading = false
          this.$refs.provider.reset();
          this.isSuccess = true;
          return;
        }
        if (uploadedFiles.length && uploadedFiles.length >= this.maxFiles) {
          this.uploading = false
          this.$refs.provider.reset();
          this.$refs.provider.setErrors(['Максимальное количество файлов: ' + this.maxFiles + '.']);
          this.isSuccess = false;
          this.clearable = true
          return;
        }

        if (this.validate) {
          this.isSuccess = false;
          const {valid} = await this.$refs.provider.validate(this.files);
          if (valid) {
            let fd = new FormData();
            for (let i = 0; i < this.files.length; i++) {
              let file = this.files[i];
              fd.append("file[" + i + "]", file);
            }
            this.$store.commit("context/CLEAR_MESSAGES");
            await this.$store
              .dispatch("filemanager/uploadFiles", {
                files: this.files,
                type: this.type,
                application_id: this.application ? this.application.id : null
              })
              .then(() => {
                if (this.messages) {
                  this.$refs.provider.setErrors(this.messages["file"]);
                } else {
                  this.$refs.provider.reset();
                  this.files = [];
                  this.isSuccess = true;
                  this.uploading = false
                }
              })
              .catch(() => {
                this.uploading = false
              });
          } else {
            this.uploading = false
          }
        }
      },
      removeImage(key) {
        this.$store.commit("filemanager/DELETE_FILES", key);
        this.files.splice(key, 1);
        this.$refs.provider.reset();
        this.$store.commit("context/CLEAR_MESSAGES");
      }
    }
  };

  localize({
    ru: {
      names: {
        files: " "
      }
    }
  });

  extend("required_file", {
    ...required,
    message: 'Необходимо выбрать и прикрепить минимум один файл',
  });
  extend("ext", {
    ...ext
  });
  extend("size", {
    ...size
  });
  extend("image", {
    ...image
  });
</script>
